import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { titleize } from 'underscore.string';
import { map, uniqBy, cloneDeep, assign, omit } from 'lodash';
import { useTranslation, withTranslation } from 'react-i18next';
import { equipmentTypes, equipmentSizes } from 'constants/Equipment';
import { getAllEquipment, bulkAddEquipment } from 'utils/EquipmentWebAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import CustomerSearchWithPreferences from 'components/site/customer-search-with-preferences';
import { getCarrierPreferencesByType } from 'utils/CarrierPreferencesUtils';
import { getReferenceNumberRequirements } from 'utils/UserServiceAPIUtils';
import { getCustomerV2 } from 'utils/CustomersAPIUtils';
import List from 'components/v2/list/List';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import PanelSection from 'components/v2/PanelSection';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getOrderMasterReferenceBookingTimeRequirement, mapOrderMasterReferenceBookingTimeRequirement, } from 'utils/OrderBookingUtils';
import CustomerSearch from '../../site/customer-search';
import Alert from '../../ui/Alert';
import Badge from '../../v2/Badge';
import ErrorAlert from '../../ui/ErrorAlert';
import LoadingCircle from '../../ui/LoadingCircle';
import AddButton from '../../v2/buttons/AddButton';
import { getAuthorizedDrivers } from '../../../utils/CarrierDriverWebAPIUtils';
import CargomaticInput from '../../v2/input';
import CargomaticSelect from '../../v2/select';
import Checkbox from '../../v2/forms/Checkbox';
import DeleteButton from '../../v2/buttons/DeleteButton';
import 'react-datepicker/dist/react-datepicker.css';
import StopForm from '../shipments/stops/StopForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, } from '@cargomatic/ui';
const MAX_SHIPMENTS = 50;
const EQUIPMENT_PAGE_SIZE = 25;
const SHIPMENT_TEMPLATE_LIMIT = 350;
function getNewStop(data) {
    return Object.assign({ action: 'pickup', sequence: 1, empty: false, off_site: false, applyEquipment: true, window_date: null, location: [{ name: '', address: '' }] }, data);
}
function getNewGroup({ origin, destination }) {
    return {
        stops: [
            getNewStop({ sequence: 1, action: 'pickup', location: [origin] }),
            getNewStop({ sequence: 2, action: 'deliver', location: [destination] }),
        ],
        consignee: null,
        hasEquipment: true,
        equipment: '',
        page: 1,
        pages: null,
        selectedEquipment: [],
        count: undefined,
    };
}
async function getAndFormatCarrierDrivers(carrierId) {
    const response = await getAuthorizedDrivers(carrierId);
    const { entity: { drivers: carrierDrivers }, } = response;
    return carrierDrivers.map((driver) => {
        const { first_name: firstName, last_name: lastName } = driver;
        const first_name = (firstName || '').trim();
        const last_name = (lastName || '').trim();
        const name = `${first_name} ${last_name}`.trim();
        return Object.assign(Object.assign({}, driver), { first_name,
            last_name,
            name });
    });
}
const mappedEquipmentTypes = map(equipmentTypes, (name, value) => ({
    name,
    value,
}));
const EquipmentRow = ({ equipment, onRemove, onChange, onApplyAll, requiredReferenceNumbers, }) => {
    const { _id, equipment_id, _equipmentType, _type, _equipmentSize, masterBillOfLading, } = equipment;
    function handleRemove() {
        onRemove(equipment_id);
    }
    function handleEquipmentIdChange(value) {
        onChange(equipment_id, 'equipment_id', value);
    }
    function handleSelectEquipmentType(item) {
        onChange(equipment_id, '_type', item.value);
    }
    function handleSelectEquipmentSize(item) {
        onChange(equipment_id, '_equipmentSize', item);
    }
    function handleMasterBillOfLadingChange(item) {
        onChange(equipment_id, 'masterBillOfLading', item);
    }
    function handleApplyAll() {
        onApplyAll(equipment_id);
    }
    function handleReferenceNumberChange(refNo, item) {
        onChange(equipment_id, refNo.reference_number, item);
    }
    const isNew = !_id;
    const { t } = useTranslation();
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
        return (React.createElement(PanelSection, null,
            React.createElement("div", { className: "justify-between items-start w-full flex flex-wrap -mx-4" },
                React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-5 gap-4 flex-1 px-4" },
                    React.createElement("div", { className: "relative flex space-x-1" },
                        isNew && (React.createElement(CargomaticInput, { type: "text", label: React.createElement("div", { className: "flex items-center space-x-1" },
                                React.createElement("span", null, t('EQUIPMENT')),
                                React.createElement(Badge, { color: "brand" }, "New")), onChange: handleEquipmentIdChange, value: equipment_id })),
                        !isNew && (React.createElement(DescriptionListItem, { label: t('EQUIPMENT') }, equipment_id))),
                    React.createElement("div", { className: "relative flex space-x-1" },
                        React.createElement(DescriptionListItem, { label: t('EQUIPMENT_TYPE') },
                            React.createElement("span", null, t(_equipmentType)))),
                    React.createElement("div", { className: "relative flex space-x-1" },
                        isNew && (React.createElement(CargomaticSelect, { items: mappedEquipmentTypes, value: _type, textKey: "name", valueKey: "value", label: t('EQUIPMENT_TYPE'), showCaption: true, onSelect: handleSelectEquipmentType })),
                        !isNew && (React.createElement("div", null,
                            React.createElement(DescriptionListItem, { label: t('EQUIPMENT_TYPE') },
                                React.createElement("span", null, t(_type)))))),
                    React.createElement("div", { className: "relative flex space-x-1" },
                        isNew && (React.createElement(CargomaticSelect, { items: equipmentSizes, value: _equipmentSize, label: t('EQUIPMENT_SIZE'), showCaption: true, onSelect: handleSelectEquipmentSize })),
                        !isNew && (React.createElement("div", null,
                            React.createElement(DescriptionListItem, { label: t('EQUIPMENT_SIZE') },
                                React.createElement("span", null, t(_equipmentSize)))))),
                    React.createElement("div", { className: "col-span-full" },
                        React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-5 gap-4" }, requiredReferenceNumbers.map((refNo) => {
                            var _a, _b;
                            return (React.createElement(CargomaticInput, { key: refNo.reference_number, label: `${t((_a = refNo === null || refNo === void 0 ? void 0 : refNo.reference_number) === null || _a === void 0 ? void 0 : _a.toUpperCase())} *`, value: (_b = equipment[refNo.reference_number]) !== null && _b !== void 0 ? _b : '', onChange: (val) => handleReferenceNumberChange(refNo, val) }));
                        })))),
                React.createElement("div", { className: "flex items-center space-x-2 px-4" },
                    React.createElement(DeleteButton, { size: "small", title: t('REMOVE_WITH_VALUE', { value: equipment_id }), onClick: handleRemove }),
                    isNew && (React.createElement("button", { className: "button button-hollow-green", type: "button", onClick: handleApplyAll }, t('APPLY_ALL')))))));
    }
    return (React.createElement("tr", { className: "" },
        React.createElement("td", null,
            React.createElement("div", { className: "relative flex space-x-1" },
                isNew && (React.createElement(CargomaticInput, { className: "w-32", hideLabel: true, onChange: handleEquipmentIdChange, value: equipment_id })),
                !isNew && React.createElement("span", null, equipment_id),
                isNew && React.createElement(Badge, { className: "", color: "brand", text: t('NEW') }))),
        React.createElement("td", null,
            React.createElement("div", { className: "relative flex space-x-1" },
                React.createElement("span", null, titleize(_equipmentType)))),
        React.createElement("td", null,
            isNew && (React.createElement(CargomaticSelect, { items: mappedEquipmentTypes, value: _type, textKey: "name", className: "w-36", valueKey: "value", hideLabel: true, showCaption: true, onSelect: handleSelectEquipmentType })),
            !isNew && React.createElement("span", null, _type)),
        React.createElement("td", null,
            React.createElement("div", { className: "w-20" },
                isNew && (React.createElement(CargomaticSelect, { items: equipmentSizes, value: _equipmentSize, hideLabel: true, showCaption: true, onSelect: handleSelectEquipmentSize })),
                !isNew && React.createElement("span", null, _equipmentSize))),
        React.createElement("td", null,
            React.createElement(CargomaticInput, { value: masterBillOfLading, onChange: handleMasterBillOfLadingChange, hideLabel: true, className: "w-36" })),
        React.createElement("td", null,
            React.createElement("div", { className: "flex items-center space-x-2" },
                React.createElement(DeleteButton, { size: "small", title: t('REMOVE_WITH_VALUE', { value: equipment_id }), onClick: handleRemove }),
                isNew && (React.createElement("button", { className: "button button-hollow-green", type: "button", onClick: handleApplyAll }, t('APPLY_ALL')))))));
};
const Group = ({ index, group, onAddStop, onFieldChange, onAddEquipment, onStopChange, onConsigneeChange, onRemoveConsignee, onRemove, onRemoveStop, canRemove, groupIndexPendingEquipment, onRemoveEquipment, onEquipmentFieldChange, onApplyAll, requiredReferenceNumbers, referenceNumbersWithoutEquipment, onReferenceNumberChange, }) => {
    function handleToggleBookingType(checked) {
        onFieldChange('hasEquipment', checked);
    }
    function handleShipmentCountChange(value) {
        onFieldChange('count', value);
    }
    function handleEquipmentChange(value) {
        onFieldChange('equipment', value);
    }
    function handleStopChange(stop) {
        onStopChange(stop);
    }
    function handleRemoveStop(stop) {
        onRemoveStop(stop);
    }
    const handleConsigneeSelect = (_value, item) => {
        onConsigneeChange({
            consignee_name: item.company_name,
            consignee: item._id,
        });
    };
    const handleConsigneeFieldChange = (value) => {
        onConsigneeChange({
            vendor_name: value,
            vendor: null,
        });
    };
    const handleRemoveConsignee = () => {
        onRemoveConsignee();
    };
    const handleReferenceNumberChanges = (refNo, value) => {
        onReferenceNumberChange(refNo, value);
    };
    const bookingTypeId = `bookingType_${index}`;
    const { t } = useTranslation();
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
        return (React.createElement("div", { className: "p-4 shadow rounded border" },
            React.createElement("div", { className: "flex justify-between items-center mb-4 border-b pb-4 -mx-8" },
                React.createElement("div", { className: "flex flex-wrap space-x-1 px-8" },
                    React.createElement("h4", { className: "my-0 flex-1 sm:w-24 w-full" }, t('GROUP_WITH_VALUE', { value: index + 1 })),
                    !group.consignee && (React.createElement("div", { className: "w-full sm:w-64" },
                        React.createElement("label", { className: "mb-1 form-label" }, t('SEARCH_CONSIGNEE')),
                        React.createElement(CustomerSearch, { clearOnSelect: false, onChange: handleConsigneeFieldChange, onSelect: handleConsigneeSelect, placeholder: t('SEARCH_FOR_CONSIGNEE'), type: "consignees", authorizedOnly: false, value: group.consignee_name }))),
                    group.consignee && (React.createElement("div", null,
                        React.createElement("label", { className: "mb-1 form-label" }, titleize(t('CONSIGNEE', { count: 1 }))),
                        React.createElement(Badge, { color: "blue", onRemove: handleRemoveConsignee, withRemove: true }, group.consignee_name)))),
                React.createElement("div", { className: "px-8" }, canRemove && (React.createElement("div", { className: "form-actions" },
                    React.createElement(DeleteButton, { onClick: onRemove }))))),
            React.createElement("div", { className: "space-y-2 divide-y divide-gray-200" },
                group.stops.map((stop, i) => (React.createElement(StopForm, { key: i, stop: stop, onStopChange: handleStopChange, onRemove: handleRemoveStop }))),
                React.createElement("a", { href: "#", className: "block pt-4 underline", onClick: onAddStop }, t('ADD_STOP'))),
            React.createElement("div", { className: "mt-6 flex flex-wrap -mx-4" },
                React.createElement(Checkbox, { className: "mb-4 px-4", name: bookingTypeId, id: bookingTypeId, checked: group.hasEquipment, onChange: handleToggleBookingType }, t('HAS_EQUIPMENT')),
                !group.hasEquipment && (React.createElement(CargomaticInput, { className: "px-4 mb-4 w-auto", type: "number", step: 1, min: 1, max: MAX_SHIPMENTS, label: t('HOW_MANY_SHIPMENTS'), value: group.count, placeholder: t('NUMBER_OF_SHIPMENTS'), onChange: handleShipmentCountChange }))),
            group.hasEquipment && (React.createElement("div", { className: "lg:w-1/2 w-full" },
                React.createElement(CargomaticInput, { className: "mb-2", type: "textarea", label: titleize(t('EQUIPMENT')), info: t('ADD_EQUIPMENT_NOTE_WITH_ADD_INFO'), value: group.equipment, placeholder: t('ADD_EQUIPMENT_NOTE'), onChange: handleEquipmentChange, rows: 5, disabled: groupIndexPendingEquipment }),
                React.createElement("div", { className: "actions" },
                    React.createElement("button", { className: "button button-hollow-green", type: "button", onClick: onAddEquipment }, titleize(t('ADD')))))),
            group.selectedEquipment.length > 0 && (React.createElement("div", { className: "col-span-full" },
                React.createElement("hr", { className: "my-6" }),
                React.createElement("h4", { className: "mb-2" }, t('SELECTED_EQUIPMENT')),
                React.createElement(Alert, { type: "info", className: "mb-4 max-w-lg", canClose: true }, t('MBL_INFO_MESSAGE')),
                React.createElement("div", { className: "overflow-x-auto" },
                    React.createElement(List, { title: "Equipment" }, group.selectedEquipment.map((equipment, i) => (React.createElement(EquipmentRow, { key: i, equipment: equipment, onRemove: onRemoveEquipment, onChange: onEquipmentFieldChange, onApplyAll: onApplyAll, requiredReferenceNumbers: requiredReferenceNumbers }))))))),
            React.createElement("div", { className: "block mb-4" }, !group.hasEquipment ? (React.createElement("div", { className: "" },
                React.createElement("label", { className: "mb-2" }, t('REQUIRED_REFERENCE_NUMBERS')),
                React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-5 gap-4" }, referenceNumbersWithoutEquipment.map((refNo) => {
                    var _a;
                    return (React.createElement(CargomaticInput, { key: refNo.reference_number, label: `${t((_a = refNo === null || refNo === void 0 ? void 0 : refNo.reference_number) === null || _a === void 0 ? void 0 : _a.toUpperCase())} *`, value: refNo.value, onChange: (value) => handleReferenceNumberChanges(refNo, value) }));
                })))) : null)));
    }
    return (React.createElement("div", { className: "p-4 shadow rounded border" },
        React.createElement("div", { className: "flex justify-between items-center mb-4 border-b pb-4 -mx-8" },
            React.createElement("div", { className: "flex flex-wrap space-x-1 px-8" },
                React.createElement("h4", { className: "my-0 flex-1 sm:w-24 w-full" }, t('GROUP_WITH_VALUE', { value: index + 1 })),
                !group.consignee && (React.createElement("div", { className: "w-full sm:w-64" },
                    React.createElement("label", { className: "mb-1 form-label" }, t('SEARCH_CONSIGNEE')),
                    React.createElement(CustomerSearch, { clearOnSelect: false, onChange: handleConsigneeFieldChange, onSelect: handleConsigneeSelect, placeholder: t('SEARCH_FOR_CONSIGNEE'), type: "consignees", authorizedOnly: false, value: group.consignee_name }))),
                group.consignee && (React.createElement("div", null,
                    React.createElement("label", { className: "mb-1 form-label" }, titleize(t('CONSIGNEE', { count: 1 }))),
                    React.createElement(Badge, { color: "blue", onRemove: handleRemoveConsignee, withRemove: true }, group.consignee_name)))),
            React.createElement("div", { className: "px-8" }, canRemove && (React.createElement("div", { className: "form-actions" },
                React.createElement(DeleteButton, { onClick: onRemove }))))),
        React.createElement("div", { className: "space-y-2 divide-y divide-gray-200" },
            group.stops.map((stop, i) => (React.createElement(StopForm, { key: i, stop: stop, onStopChange: handleStopChange, onRemove: handleRemoveStop }))),
            React.createElement("a", { href: "#", className: "block pt-4 underline", onClick: onAddStop }, t('ADD_STOP'))),
        React.createElement("div", { className: "mt-4 grid grid-cols-2 gap-4" },
            React.createElement(Checkbox, { className: "mb-4", name: bookingTypeId, id: bookingTypeId, checked: group.hasEquipment, onChange: handleToggleBookingType }, t('HAS_EQUIPMENT')),
            group.hasEquipment && (React.createElement("div", { className: "col-span-full" },
                React.createElement(CargomaticInput, { className: "mb-2", type: "textarea", label: titleize(t('EQUIPMENT')), info: t('ADD_EQUIPMENT_NOTE_WITH_ADD_INFO'), value: group.equipment, placeholder: t('ADD_EQUIPMENT_NOTE'), onChange: handleEquipmentChange, rows: 5, disabled: groupIndexPendingEquipment }),
                React.createElement("div", { className: "actions" },
                    React.createElement("button", { className: "button button-hollow-green", type: "button", onClick: onAddEquipment }, titleize(t('ADD')))))),
            group.selectedEquipment.length > 0 && (React.createElement("div", { className: "col-span-full" },
                React.createElement("h4", { className: "mb-2" }, t('SELECTED_EQUIPMENT')),
                React.createElement(Alert, { type: "info", className: "mb-4 max-w-lg", canClose: true }, t('MBL_INFO_MESSAGE')),
                React.createElement("div", { className: "overflow-x-auto" },
                    React.createElement("table", { className: "default-table" },
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null, titleize(t('EQUIPMENT_ID'))),
                                React.createElement("th", null, t('ASSET_TYPE')),
                                React.createElement("th", null, titleize(t('TYPE'))),
                                React.createElement("th", null, titleize(t('SIZE', { count: 1 }))),
                                React.createElement("th", null, t('MASTER_BOL')),
                                React.createElement("th", null, "\u00A0"))),
                        React.createElement("tbody", null, group.selectedEquipment.map((equipment, i) => (React.createElement(EquipmentRow, { key: i, equipment: equipment, onRemove: onRemoveEquipment, onChange: onEquipmentFieldChange, onApplyAll: onApplyAll })))))))),
            !group.hasEquipment && (React.createElement(CargomaticInput, { className: "mb-4 sm:w-24 w-auto", type: "number", step: 1, min: 1, max: MAX_SHIPMENTS, label: t('HOW_MANY_SHIPMENTS'), info: t('ADD_EQUIPMENT_NOTE'), value: group.count, placeholder: t('NUMBER_OF_SHIPMENTS'), onChange: handleShipmentCountChange })))));
};
function getInitialState(props) {
    const { origin, destination } = props;
    return {
        groups: [getNewGroup({ origin, destination })],
        enablePortEvents: true,
        carrier: null,
        driver: null,
        isCarrierTrained: true,
        saving: false,
        errors: {},
        success: false,
        meta: {
            carrier: '',
            driver: '',
        },
        carrierPreferences: {
            preferred: [],
            excluded: [],
        },
        drivers: [],
        templates: [],
        templateId: null,
        templateName: '',
        saveAsToggled: false,
        request: {},
        groupIndexPendingEquipment: null,
        requiredReferenceNumbers: [],
        requiredAtBooking: [],
        referenceNumbersWithoutEquipment: [],
        templateFetchPending: true,
        selectedTemplate: null,
    };
}
function applyTemplate(template) {
    var _a, _b, _c;
    const groupsData = template.groups.map((group) => {
        var _a, _b;
        var groupData = omit(group, ['stops', 'consignee']);
        var stopsData = group.stops.map((stop) => {
            var location = [
                { name: stop.location_name, address: stop.location_address },
            ];
            var stopData = assign(omit(stop, ['location_address', 'location_name']), {
                location,
            });
            return getNewStop(stopData);
        });
        const restGroupData = {
            stops: stopsData,
            consignee: (_a = group.consignee) === null || _a === void 0 ? void 0 : _a._id,
            consignee_name: titleize((_b = group.consignee) === null || _b === void 0 ? void 0 : _b.optimized_name),
            selectedEquipment: [],
        };
        return assign(groupData, restGroupData);
    });
    const driverData = assign(template.driver, {
        name: titleize((_a = template.driver) === null || _a === void 0 ? void 0 : _a.optimized_name),
    });
    return {
        groups: groupsData,
        enablePortEvents: template.enable_port_events,
        carrier: (_b = template.carrier) === null || _b === void 0 ? void 0 : _b._id,
        driver: (_c = template.driver) === null || _c === void 0 ? void 0 : _c._id,
        meta: {
            carrier: template.carrier,
            driver: driverData,
        },
        templateId: template._id,
        templateName: template.name,
    };
}
const ShipperTemplatesDropdown = ({ templates, selectedTemplate, onSelect, isLoading, t, }) => {
    if (isLoading) {
        return React.createElement(LoadingCircle, { text: t('LOADING') });
    }
    if (!(templates === null || templates === void 0 ? void 0 : templates.length)) {
        return (React.createElement(Alert, { type: "warning" }, t('NO_ORDER_TEMPLATE_FOUND_FOR_SHIPPER')));
    }
    return (React.createElement("select", { className: "form-select w-64", value: (selectedTemplate === null || selectedTemplate === void 0 ? void 0 : selectedTemplate._id) || '', onChange: onSelect, "data-testid": "book-multiple-shipments-template-select" },
        React.createElement("option", { value: "" }, t('POPULATE_TEMPLATE')),
        templates.map((template) => (React.createElement("option", { value: template._id, key: template._id, "data-testid": `template-${template._id}` }, template.name)))));
};
class BulkingBookingForm extends Component {
    constructor(props) {
        super(props);
        this.getCarrierPreferences = async (shipperId) => {
            const carrierPreferences = await getCarrierPreferencesByType(shipperId, 'drayage');
            this.setState({ carrierPreferences });
        };
        this.getCarrierDetails = async (carrierId) => {
            const carrier = await getCustomerV2(carrierId);
            this.setState((prevState) => {
                const { meta } = prevState;
                return {
                    carrier: carrierId,
                    isCarrierTrained: carrier.status === 'Trained',
                    meta: Object.assign(Object.assign({}, meta), { carrier }),
                };
            }, this.getCarrierDefaultDriver);
        };
        this.getCarrierDrivers = async () => getAndFormatCarrierDrivers(this.state.carrier);
        this.getCarrierDefaultDriver = async () => {
            var _a, _b;
            const { meta: { carrier } = {} } = this.state;
            const drivers = await this.getCarrierDrivers();
            const defaultDriverId = (_b = (_a = carrier.default_driver) === null || _a === void 0 ? void 0 : _a._id) !== null && _b !== void 0 ? _b : carrier.default_driver;
            const defaultDriver = drivers.find((drvr) => drvr._id === defaultDriverId);
            console.log('~~~njm', { carrier, defaultDriver, drivers });
            this.setState((prevState) => {
                return Object.assign(Object.assign({}, prevState), { drivers, driver: defaultDriver === null || defaultDriver === void 0 ? void 0 : defaultDriver._id, meta: Object.assign(Object.assign({}, prevState.meta), { driver: defaultDriver }) });
            });
        };
        this.getShipperReferenceNumberRequirements = async (shipperId) => {
            var _a;
            try {
                const response = await getReferenceNumberRequirements(shipperId);
                const requiredReferenceNumbers = (((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumberRequirementsQuery) || []).map((refNo) => {
                    var _a;
                    return Object.assign(Object.assign({}, refNo), { reference_number: (_a = refNo === null || refNo === void 0 ? void 0 : refNo.reference_number) === null || _a === void 0 ? void 0 : _a.slug, value: '' });
                });
                return requiredReferenceNumbers;
            }
            catch (e) {
                console.error(e);
            }
        };
        this.setShipperReferenceNumberRequirements = (referenceNumbers) => {
            const { order } = this.props;
            if (order) {
                const { order_type } = order;
                const bookingTimeRequirements = referenceNumbers.filter((refNo) => {
                    return refNo.required_at === 'BOOKING_TIME';
                });
                const requiredAtBooking = getOrderMasterReferenceBookingTimeRequirement(bookingTimeRequirements, order_type);
                const allRefNums = featureEnabled(CONFIGURED_FEATURES.SSR)
                    ? mapOrderMasterReferenceBookingTimeRequirement(requiredAtBooking, order_type)
                    : [];
                this.setState({
                    requiredAtBooking,
                    requiredReferenceNumbers: referenceNumbers,
                    referenceNumbersWithoutEquipment: allRefNums,
                });
            }
        };
        this.isSaveDisabled = () => {
            return this.state.saving || this.state.groupIndexPendingEquipment !== null;
        };
        this.handleTemplateSelect = async (e) => {
            const templateId = e.target.value;
            const { t } = this.props;
            try {
                if (!templateId) {
                    return this.setState({
                        selectedTemplate: null,
                        templateName: '',
                    });
                }
                const template = await this.getShipmentTemplateData(templateId);
                if (template) {
                    const modalData = applyTemplate(template);
                    this.setState(Object.assign(Object.assign({}, modalData), { selectedTemplate: template, templateName: template.name }), async () => {
                        if (this.state.carrier) {
                            const drivers = await this.getCarrierDrivers();
                            this.setState({ drivers });
                        }
                    });
                }
                else {
                    this.setState({
                        selectedTemplate: null,
                        templateName: '',
                    });
                }
            }
            catch (e) {
                console.error(e);
                showToast(t('SHIPMENT'), t('FAILED_TO_FETCH_SHIPMENT_TEMPLATES'), 'error');
            }
        };
        this.handleTemplateNameChange = (e) => {
            this.setState((prevState) => {
                return Object.assign(Object.assign({}, prevState), { templateName: e });
            });
        };
        this.handleToggleSaveAs = () => this.setState({
            saveAsToggled: true,
        });
        this.handleAddGroupEquipment = (groupIndex) => () => {
            const { equipment } = this.state.groups[groupIndex];
            if (!equipment) {
                return;
            }
            this.setState({ groupIndexPendingEquipment: groupIndex }, async () => {
                const containerMasterBillOfLadingPairs = parseContainerMasterBillOfLadingPairs(equipment);
                const equipmentRequested = containerMasterBillOfLadingPairs.map((pair) => pair.container);
                const res = await getAllEquipment({
                    select: '_id equipment_id _equipmentType _type _equipmentSize',
                    per_page: 200,
                    equipment_id: equipmentRequested,
                });
                const { entity: { items }, } = res;
                const eqMap = items.reduce((runningMap, eq) => {
                    runningMap[eq.equipment_id] = eq;
                    return runningMap;
                }, {});
                const mappedEquipment = equipmentRequested.map((eq) => {
                    const mapped = Object.assign({ equipment_id: eq }, (eqMap[eq] || {}));
                    if (!mapped._id) {
                        mapped._equipmentType = 'container';
                    }
                    return mapped;
                });
                this.setState((state) => {
                    const target = cloneDeep(state.groups[groupIndex]);
                    target.equipment = '';
                    target.selectedEquipment = uniqBy([...target.selectedEquipment, ...mappedEquipment], (eq) => eq.equipment_id).map((eq) => {
                        var _a, _b;
                        const equipObj = Object.assign(Object.assign({}, eq), { masterBillOfLading: eq.masterBillOfLading
                                ? eq.masterBillOfLading
                                : (_a = containerMasterBillOfLadingPairs.find((pair) => pair.container === eq.equipment_id)) === null || _a === void 0 ? void 0 : _a.masterBillOfLading, master_bol: eq.masterBillOfLading
                                ? eq.masterBillOfLading
                                : (_b = containerMasterBillOfLadingPairs.find((pair) => pair.container === eq.equipment_id)) === null || _b === void 0 ? void 0 : _b.masterBillOfLading });
                        state.requiredAtBooking.forEach((refNo) => {
                            equipObj[refNo.reference_number] = (equipObj === null || equipObj === void 0 ? void 0 : equipObj[refNo.reference_number])
                                ? equipObj[refNo.reference_number]
                                : '';
                        });
                        return equipObj;
                    });
                    target.page = 1;
                    target.pages = Math.ceil(target.selectedEquipment.length / EQUIPMENT_PAGE_SIZE);
                    return {
                        groupIndexPendingEquipment: null,
                        groups: [
                            ...state.groups.slice(0, groupIndex),
                            target,
                            ...state.groups.slice(groupIndex + 1),
                        ],
                    };
                });
            });
        };
        this.handleCloseAlert = () => {
            this.setState({ success: false });
        };
        this.handleCloseErrorAlert = () => {
            this.setState({ errors: {} });
        };
        this.handleAddGroup = () => {
            const { origin, destination } = this.props;
            this.setState((state) => ({
                groups: [...state.groups, getNewGroup({ origin, destination })],
            }));
        };
        this.handleRemoveGroup = (index) => () => {
            this.setState((state) => ({
                groups: [
                    ...state.groups.slice(0, index),
                    ...state.groups.slice(index + 1),
                ],
            }));
        };
        this.handleAddGroupStop = (groupIndex) => () => {
            this.setState((prevState) => {
                var _a;
                const target = cloneDeep(prevState.groups[groupIndex]);
                const newSequence = (((_a = target.stops[target.stops.length - 1]) === null || _a === void 0 ? void 0 : _a.sequence) || 0) + 1;
                target.stops = [...target.stops, getNewStop({ sequence: newSequence })];
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleRemoveGroupStop = (groupIndex) => (stop) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const stopIndex = target.stops.findIndex((s) => s.sequence === stop.sequence);
                target.stops = [
                    ...target.stops.slice(0, stopIndex),
                    ...target.stops.slice(stopIndex + 1),
                ].map((stop, i) => (Object.assign(Object.assign({}, stop), { sequence: i + 1 })));
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleGroupConsigneeChange = (groupIndex) => ({ consignee_name, consignee }) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                target.consignee = consignee;
                target.consignee_name = consignee_name;
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleRemoveGroupConsignee = (groupIndex) => () => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                target.consignee = null;
                target.consignee_name = '';
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleRemoveGroupEquipment = (groupIndex) => (equipmentId) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const equipmentIndex = target.selectedEquipment.findIndex((eq) => eq.equipment_id === equipmentId);
                target.selectedEquipment = [
                    ...target.selectedEquipment.slice(0, equipmentIndex),
                    ...target.selectedEquipment.slice(equipmentIndex + 1),
                ];
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleGroupEquipmentApplyAll = (groupIndex) => (equipmentId) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const equipmentIndex = target.selectedEquipment.findIndex((eq) => eq.equipment_id === equipmentId);
                const targetEquipment = target.selectedEquipment[equipmentIndex];
                target.selectedEquipment = target.selectedEquipment.map((eq, i) => {
                    if (eq._id || i === equipmentIndex) {
                        return eq;
                    }
                    let _type = eq._type;
                    let _equipmentSize = eq._equipmentSize;
                    if (!_type) {
                        _type = targetEquipment._type;
                    }
                    if (!_equipmentSize) {
                        _equipmentSize = targetEquipment._equipmentSize;
                    }
                    return Object.assign(Object.assign({}, eq), { _type,
                        _equipmentSize });
                });
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleGroupStopChange = (groupIndex) => (stop) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const stopIndex = target.stops.findIndex((s) => s.sequence === stop.sequence);
                target.stops = [
                    ...target.stops.slice(0, stopIndex),
                    stop,
                    ...target.stops.slice(stopIndex + 1),
                ];
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleGroupEquipmentFieldChange = (groupIndex) => (equipmentId, field, value) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const equipmentIndex = target.selectedEquipment.findIndex((eq) => eq.equipment_id === equipmentId);
                const equipment = Object.assign(Object.assign({}, target.selectedEquipment[equipmentIndex]), { [field]: value });
                if (field === '_equipmentType' && value === 'container') {
                    equipment._type = null;
                    equipment._equipmentSize = null;
                }
                target.selectedEquipment = [
                    ...target.selectedEquipment.slice(0, equipmentIndex),
                    equipment,
                    ...target.selectedEquipment.slice(equipmentIndex + 1),
                ];
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleGroupFieldChange = (groupIndex) => (field, value) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                target[field] = value;
                if (field === 'hasEquipment' && !value) {
                    target.equipment = '';
                    target.selectedEquipment = [];
                    target.count = 1;
                }
                else if (field === 'hasEquipment' && value) {
                    target.count = 0;
                }
                if (field === 'count') {
                    target.count = parseInt(value, 10) || 0;
                }
                const groups = [
                    ...prevState.groups.slice(0, groupIndex),
                    target,
                    ...prevState.groups.slice(groupIndex + 1),
                ];
                const updates = {
                    groups,
                };
                return updates;
            });
        };
        this.handleReferenceNumberChange = (refNo, value) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.referenceNumbersWithoutEquipment);
                const index = target.findIndex((ref) => ref.reference_number === refNo.reference_number);
                if (index > -1) {
                    target[index].value = value;
                }
                return Object.assign(Object.assign({}, prevState), { referenceNumbersWithoutEquipment: target });
            });
        };
        this.handleGroupAddressChange = (groupIndex) => (locationIndex, field, value) => {
            this.setState((prevState) => {
                const target = cloneDeep(prevState.groups[groupIndex]);
                const location = Object.assign(Object.assign({}, target.locations[locationIndex]), { [field]: value });
                target.locations = [
                    ...target.locations.slice(0, locationIndex),
                    location,
                    ...target.locations.slice(locationIndex + 1),
                ];
                return {
                    groups: [
                        ...prevState.groups.slice(0, groupIndex),
                        target,
                        ...prevState.groups.slice(groupIndex + 1),
                    ],
                };
            });
        };
        this.handleSelectCarrier = (_value, item) => {
            this.setState((prevState) => {
                const { meta } = prevState;
                return {
                    carrier: item._id,
                    isCarrierTrained: item.status === 'Trained',
                    meta: Object.assign(Object.assign({}, meta), { carrier: item, driver: '' }),
                };
            }, this.getCarrierDefaultDriver);
        };
        this.handleSelectDriver = (_value, item) => {
            this.setState((prevState) => {
                const { meta } = prevState;
                const { _id: driver, carrierData: carrier } = item;
                return {
                    carrier: carrier._id,
                    isCarrierTrained: carrier.status === 'Trained',
                    driver,
                    meta: Object.assign(Object.assign({}, meta), { driver: item, carrier }),
                };
            }, async () => {
                if (this.state.carrier) {
                    const drivers = await this.getCarrierDrivers();
                    this.setState({ drivers });
                }
            });
        };
        this.handleDriverDropdownChange = (e) => {
            const { target: { value: driver }, } = e;
            const item = this.state.drivers.find((drvr) => drvr._id === driver);
            this.setState((prevState) => {
                return Object.assign(Object.assign({}, prevState), { driver, meta: Object.assign(Object.assign({}, prevState.meta), { driver: item }) });
            });
        };
        this.handleRemoveCarrier = () => {
            this.setState((prevState) => {
                const { meta } = prevState;
                return {
                    carrier: null,
                    driver: null,
                    isCarrierTrained: true,
                    meta: Object.assign(Object.assign({}, meta), { carrier: '', driver: '' }),
                };
            });
        };
        this.handleRemoveDriver = () => {
            this.setState((prevState) => {
                const { meta } = prevState;
                return {
                    driver: null,
                    meta: Object.assign(Object.assign({}, meta), { driver: '' }),
                };
            });
        };
        this.handleTogglePortEvents = (enablePortEvents) => {
            this.setState({ enablePortEvents });
        };
        this.handleSave = (updateTemplate = false) => {
            const { t } = this.props;
            this.setState((prevState) => {
                const errors = this.validate(prevState);
                const saving = Object.keys(errors).length === 0;
                return {
                    errors,
                    saving,
                };
            }, async () => {
                if (Object.keys(this.state.errors).length > 0) {
                    return;
                }
                const newEquipment = uniqBy(this.state.groups.reduce((list, job) => {
                    if (!job.hasEquipment) {
                        return list;
                    }
                    return list.concat(job.selectedEquipment.filter((eq) => !eq._id));
                }, []), (eq) => eq.equipment_id);
                let bulkAddResults;
                let equipment = [];
                try {
                    if (newEquipment.length > 0) {
                        bulkAddResults = await bulkAddEquipment(newEquipment);
                        equipment = bulkAddResults.entity;
                    }
                }
                catch (e) {
                    console.log(e);
                    const error = e.entity.error;
                    if (error.code === 11000) {
                        const insertErrors = error.writeErrors.reduce((errorMap, writeError) => {
                            if (writeError.code === 11000 &&
                                writeError.errmsg.includes('equipment_id')) {
                                const { op: { equipment_id }, } = writeError;
                                errorMap[equipment_id] = t('EQUIPMENT_ALREADY_EXISTS', {
                                    equipment_id,
                                });
                                return errorMap;
                            }
                            return errorMap;
                        }, {});
                        if (Object.keys(insertErrors).length > 0) {
                            this.setState({ errors: insertErrors, saving: false });
                            return;
                        }
                    }
                }
                const request = this.transformRequest(equipment);
                if (updateTemplate) {
                    try {
                        const { templateId, templateName } = this.state;
                        if (templateId) {
                            await ShipmentWebAPIUtils.saveTemplateAndBookShipments(this.props.orderId, templateId, templateName, request);
                        }
                        else {
                            await ShipmentWebAPIUtils.createShipmentsAndTemplate(this.props.orderId, templateName, request);
                        }
                        const initialState = getInitialState(this.props);
                        this.setState(Object.assign(Object.assign({}, initialState), { saving: false, success: true }));
                        this.props.onSaveSuccess();
                    }
                    catch (e) {
                        console.error(e);
                        const errors = {
                            summary: t('ERROR_ON_SHIPMENTS_BOOKING_WITH_MESSAGE', {
                                message: e.message,
                            }),
                        };
                        showToast(titleize(t('SHIPMENT')), t('SAVE_TEMPLATE_AND_BOOK_SHIPMENT_ERROR'), 'error');
                        this.setState({ errors, saving: false, success: false });
                    }
                }
                else {
                    try {
                        await ShipmentWebAPIUtils.bookMultipleOrderShipments(this.props.orderId, request);
                        const initialState = getInitialState(this.props);
                        this.setState(Object.assign(Object.assign({}, initialState), { saving: false, success: true }));
                        this.props.onSaveSuccess();
                    }
                    catch (e) {
                        console.error(e);
                        const errors = {
                            summary: t('ERROR_ON_SHIPMENTS_BOOKING_WITH_MESSAGE', {
                                message: e.message,
                            }),
                        };
                        this.setState({ errors, saving: false, success: false });
                    }
                }
            });
        };
        this.handleCancel = () => {
            this.props.onHide();
        };
        this.renderShipperTemplatesDropdown = (templates = []) => {
            const { t } = this.props;
            return (React.createElement(ShipperTemplatesDropdown, { templates: templates, selectedTemplate: this.state.selectedTemplate, onSelect: this.handleTemplateSelect, isLoading: this.state.templateFetchPending, t: t }));
        };
        this.state = Object.assign(Object.assign({}, getInitialState(props)), { selectedTemplate: null });
    }
    async getShipmentTemplatesData(params) {
        const { t } = this.props;
        try {
            const response = await ShipmentWebAPIUtils.getShipmentTemplates(params);
            this.setState({
                templates: response.templates,
                templateFetchPending: false,
            });
        }
        catch (e) {
            console.log(e);
            showToast(t('SHIPMENT'), t('FAILED_TO_FETCH_SHIPMENT_TEMPLATES'), 'error');
        }
    }
    async componentDidMount() {
        const { shipperId, carrier } = this.props;
        const carrierId = carrier === null || carrier === void 0 ? void 0 : carrier._id;
        const limit = SHIPMENT_TEMPLATE_LIMIT;
        this.getShipmentTemplatesData({
            limit,
            shipper_id: shipperId,
            is_active: true,
        });
        this.getCarrierPreferences(shipperId);
        if (carrierId)
            this.getCarrierDetails(carrierId);
        if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
            const requiredReferenceNumbers = await this.getShipperReferenceNumberRequirements(shipperId);
            this.setShipperReferenceNumberRequirements(requiredReferenceNumbers);
        }
    }
    async getShipmentTemplateData(templateId) {
        try {
            const response = await ShipmentWebAPIUtils.getShipmentTemplate(templateId);
            return response;
        }
        catch (e) {
            console.log(e);
        }
    }
    validate(request) {
        const errors = {};
        const { groups } = request;
        const { t } = this.props;
        const flattenedStops = groups.reduce((groupStops, group) => {
            return groupStops.concat(...group.stops);
        }, []);
        if (!this.state.isCarrierTrained) {
            errors.carrier = t('CARRIER_IS_NOT_TRAINED');
        }
        if (!flattenedStops.every((stop) => stop.location && stop.location[0].address)) {
            errors.groups = t('PROVIDE_ADDRESS_TO_EACH_STOP');
        }
        const groupsWithEquipment = groups.filter((dest) => dest.hasEquipment);
        if (!groupsWithEquipment.every((dest) => dest.selectedEquipment.length > 0)) {
            errors.equipment = t('PROVIDE_LIST_EQUIPMENT_TO_GROUPS');
        }
        else if (!groupsWithEquipment.every((dest) => {
            return dest.selectedEquipment.every((eq) => eq._equipmentType === 'container');
        })) {
            errors.equipment = t('ONLY_CONTAINERS_ALLOWED');
        }
        else if (!groupsWithEquipment.every((dest) => {
            return dest.selectedEquipment
                .filter((eq) => !eq._id)
                .every((eq) => eq._type && eq._equipmentSize);
        })) {
            errors.equipment = t('CONTAINER_MUST_HAVE_TYPE_AND_SIZE');
        }
        const groupsWithCounts = groups.filter((dest) => !dest.hasEquipment);
        if (!groupsWithCounts.every((dest) => dest.count > 0)) {
            errors.count = t('SHIPMENTS_REQUIRED_EQUIPMENT_ERROR');
        }
        const count = groupsWithCounts.reduce((sum, group) => {
            return sum + (group.count || 0);
        }, 0);
        const containerCount = groupsWithEquipment.reduce((sum, group) => {
            return sum + (group.selectedEquipment || []).length;
        }, 0);
        if (count + containerCount > MAX_SHIPMENTS) {
            errors.maxCount = t('MAX_SHIPMENTS_WITH_VALUE', { value: MAX_SHIPMENTS });
        }
        return errors;
    }
    transformRequest(newEquipment) {
        const { carrier, driver, enablePortEvents: enable_port_events, groups, referenceNumbersWithoutEquipment, } = this.state;
        const eqMap = newEquipment.reduce((runningMap, eq) => {
            runningMap[eq.equipment_id] = eq._id;
            return runningMap;
        }, {});
        const refNos = referenceNumbersWithoutEquipment.reduce((reducer, refNo) => {
            reducer[refNo.reference_number] = refNo.value;
            return reducer;
        }, {});
        return {
            shipper: this.props.shipperId,
            carrier,
            driver,
            enable_port_events,
            shipments: groups.map((d) => {
                const { consignee, stops, count: countStr, hasEquipment } = d;
                const count = parseInt(countStr, 10) || 0;
                return {
                    consignee,
                    equipment: d.selectedEquipment.map((eq) => {
                        let id;
                        if (eq._id) {
                            id = eq._id;
                        }
                        else {
                            id = eqMap[eq.equipment_id];
                        }
                        const { masterBillOfLading, master_bol, equipment_id } = eq;
                        const returnObj = {
                            _id: id,
                            masterBillOfLading,
                            equipment_id,
                            master_bol,
                        };
                        this.state.requiredAtBooking.forEach((refNo) => {
                            returnObj[refNo.reference_number] = eq[refNo.reference_number];
                        });
                        return returnObj;
                    }),
                    count,
                    hasEquipment,
                    stops,
                    referenceNumbers: refNos,
                };
            }),
        };
    }
    renderSuccess() {
        const { success } = this.state;
        if (!success) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement(Alert, { type: "success", title: t('BOOKING_SUCCESSFUL'), canClose: true, onClose: this.handleCloseAlert }, t('SHIPMENTS_SUCCESSFULLY_BOOKED')));
    }
    renderErrors() {
        const errorKeys = Object.keys(this.state.errors);
        if (errorKeys.length === 0) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement("div", { className: "mb-4" },
            React.createElement(ErrorAlert, { title: t('ADDRESS_ISSUES'), onClose: this.handleCloseErrorAlert, canClose: true },
                React.createElement("ul", { className: "ml-0 list-reset" }, errorKeys.map((errorKey) => (React.createElement("li", { key: errorKey }, this.state.errors[errorKey])))))));
    }
    renderShipmentTemplateActions() {
        if (!SessionStore.hasOrderTemplateAdminRole()) {
            return null;
        }
        const { t } = this.props;
        const buttons = [];
        if (this.state.selectedTemplate && !this.state.saveAsToggled) {
            buttons.push(React.createElement("div", { className: "px-2 mb-4 sm:mb-0", key: "shipment-save-order-template-button" },
                React.createElement("button", { id: "shipment-saveas-button", className: "block mr-2 button button-hollow-green", onClick: this.handleToggleSaveAs }, t('SAVE_AS'))));
        }
        if (!this.state.selectedTemplate || this.state.saveAsToggled) {
            buttons.push(React.createElement("div", { className: "w-64 px-2 mb-4 sm:mb-0", key: "shipment-create-order-template-input" },
                React.createElement(CargomaticInput, { type: "text", name: "template-name", className: "mb-2", placeholder: t('TEMPLATE_NAME'), disabled: this.isSaveDisabled(), value: this.state.templateName, onChange: this.handleTemplateNameChange })));
        }
        buttons.push(React.createElement("div", { className: "px-2 mb-4 sm:mb-0", key: "shipment-create-order-template-button" },
            React.createElement("button", { id: "shipment-create-order-template-button", className: "block mr-2 button button-hollow-green", disabled: this.isSaveDisabled() || !this.state.templateName, onClick: () => {
                    this.handleSave(true);
                } },
                this.state.selectedTemplate && (React.createElement("span", null, t('SAVE_AND_BOOK_SHIPMENTS'))),
                !this.state.selectedTemplate && (React.createElement("span", null, t('CREATE_AND_BOOK_SHIPMENTS'))))));
        return React.createElement("div", { className: "flex items-center" }, buttons);
    }
    render() {
        var _a;
        const { t } = this.props;
        const { carrierPreferences, templates } = this.state;
        return (React.createElement(Dialog, { open: this.props.show, onOpenChange: this.props.onHide, className: "z-50" },
            React.createElement(DialogContent, { className: "w-screen h-screen max-w-none max-h-none overflow-y-auto" },
                React.createElement(DialogHeader, null,
                    React.createElement("div", { className: "flex content-center justify-between" },
                        React.createElement("div", { className: "flex items-center" },
                            React.createElement(DialogTitle, { id: "create-shipment-header", className: "text-grey-darker whitespace-nowrap modal-title mr-4" }, t('BOOK_MULTIPLE_SHIPMENTS')),
                            this.renderShipperTemplatesDropdown(templates)))),
                React.createElement("div", { className: "mb-4" },
                    this.renderSuccess(),
                    this.renderErrors(),
                    React.createElement("div", { className: "flex -mx-4" },
                        React.createElement("div", { className: "px-4 mb-4" },
                            React.createElement("label", { className: "block mb-2 form-label" }, t('SELECR_A_CARRIER')),
                            !this.state.carrier && (React.createElement(CustomerSearchWithPreferences, { clearOnSelect: false, onSelect: this.handleSelectCarrier, placeholder: t('SEARCH_FOR_A_CARRIER'), type: "carriers", authorizedOnly: true, carrierPreferences: carrierPreferences, isDriver: false })),
                            this.state.carrier && (React.createElement("div", { className: "flex items-start justify-start" },
                                React.createElement(Badge, { onRemove: this.handleRemoveCarrier, withRemove: true, color: "green", text: this.state.meta.carrier.name })))),
                        React.createElement("div", { className: "px-4 mb-4" },
                            React.createElement("label", { className: "block mb-2 form-label" }, titleize(t('SELECT_A_DRIVER'))),
                            !this.state.carrier && (React.createElement(CustomerSearchWithPreferences, { clearOnSelect: false, onSelect: this.handleSelectDriver, placeholder: t('SEARCH_FOR_A_DRIVER'), type: "drivers", authorizedOnly: true, carrierPreferences: carrierPreferences, isDriver: true })),
                            this.state.carrier &&
                                this.state.driver &&
                                this.state.driver.length && (React.createElement("div", { className: "flex items-start justify-start" },
                                React.createElement(Badge, { onRemove: this.handleRemoveDriver, withRemove: true, color: "green", text: (_a = this.state.meta.driver) === null || _a === void 0 ? void 0 : _a.name }))),
                            this.state.carrier &&
                                this.state.drivers &&
                                !this.state.driver && (React.createElement("select", { className: "mb-4 form-field", value: this.state.driver, onChange: this.handleDriverDropdownChange },
                                React.createElement("option", { value: "" },
                                    titleize(t('SELECT_A_DRIVER')),
                                    "*"),
                                this.state.drivers.map((driver) => (React.createElement("option", { key: driver._id, value: driver._id }, driver.name))))))),
                    React.createElement("div", { className: "my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement("h3", { className: "my-0" }, titleize(t('GROUP', { count: 2 }))),
                            React.createElement(AddButton, { onClick: this.handleAddGroup })),
                        React.createElement("div", { className: "space-y-4" }, this.state.groups.map((group, i) => (React.createElement(Group, { key: i, index: i, group: group, onAddressChange: this.handleGroupAddressChange(i), onFieldChange: this.handleGroupFieldChange(i), onConsigneeChange: this.handleGroupConsigneeChange(i), onRemoveConsignee: this.handleRemoveGroupConsignee(i), onStopChange: this.handleGroupStopChange(i), onApplyAll: this.handleGroupEquipmentApplyAll(i), onAddEquipment: this.handleAddGroupEquipment(i), onAddStop: this.handleAddGroupStop(i), onRemoveStop: this.handleRemoveGroupStop(i), onRemoveEquipment: this.handleRemoveGroupEquipment(i), onEquipmentFieldChange: this.handleGroupEquipmentFieldChange(i), onRemove: this.handleRemoveGroup(i), canRemove: i !== 0, equipmentEditingDisabled: this.state.groupIndexPendingEquipment &&
                                this.state.groupIndexPendingEquipment !== i, requiredReferenceNumbers: this.state.requiredAtBooking, referenceNumbersWithoutEquipment: this.state.referenceNumbersWithoutEquipment, onReferenceNumberChange: this.handleReferenceNumberChange }))))),
                    React.createElement(Checkbox, { checked: this.state.enablePortEvents, onChange: this.handleTogglePortEvents, className: "my-4", id: "port_events", name: "port_events" }, t('ENABLE_PORT_INTEGRATION')),
                    React.createElement("div", { className: "mt-6 form-actions" },
                        React.createElement("div", { className: "flex justify-end items-center" },
                            this.renderShipmentTemplateActions(),
                            React.createElement("button", { type: "button", onClick: () => {
                                    this.handleSave(false);
                                }, disabled: this.isSaveDisabled(), className: "block mr-2 button button-green" },
                                this.state.saving && React.createElement(LoadingCircle, { text: t('BOOKING') }),
                                !this.state.saving && React.createElement("span", null, t('BOOK_SHIPMENTS'))),
                            React.createElement("button", { type: "button", onClick: this.handleCancel, disabled: this.isSaveDisabled(), className: "button button-secondary" }, titleize(t('CANCEL')))))))));
    }
}
function convertStringToArray(value) {
    if (!value) {
        return [];
    }
    return value.split('\n');
}
function parseContainerMasterBillOfLadingPairs(value) {
    const rows = convertStringToArray(value);
    return rows
        .map((str) => {
        var _a, _b;
        const items = str
            .replace(',', '\t')
            .split('\t')
            .map((item) => item === null || item === void 0 ? void 0 : item.trim());
        return {
            container: ((_a = items === null || items === void 0 ? void 0 : items[0]) === null || _a === void 0 ? void 0 : _a.trim()) || '',
            masterBillOfLading: ((_b = items === null || items === void 0 ? void 0 : items[1]) === null || _b === void 0 ? void 0 : _b.trim()) || '',
        };
    })
        .filter((pair) => pair.container);
}
BulkingBookingForm.propTypes = {
    orderId: PropTypes.string.isRequired,
    onHide: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired,
    onSaveSuccess: PropTypes.func.isRequired,
    shipperId: PropTypes.string.isRequired,
};
export default withTranslation()(BulkingBookingForm);
